import { createFileRoute, Navigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useEffect, useEffectEvent } from "react";
import { z } from "zod";

import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { computeEffectiveDateRange } from "@/app/lib/utils/computeEffectiveDateRange";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { CabinClass, TripType } from "@/types/enums";

const flightSearchSchema = z
  .object({
    // Static dates
    departure: z.coerce.date().optional(),
    return: z.coerce.date().optional(),

    // Offset-based dates
    departureOffset: z.coerce.number().min(0).optional(),
    tripLength: z.coerce.number().min(0).optional(),

    // Location data
    originId: z.coerce.string(),
    originName: z.string(),
    originCountry: z.string(),
    destinationId: z.coerce.string(),
    destinationName: z.string(),
    destinationCountry: z.string(),

    // Passengers
    adults: fallback(z.coerce.number(), 1).default(1),
    children: fallback(z.coerce.number(), 0).default(0),
    infants: fallback(z.coerce.number(), 0).default(0),

    // Other options
    tripType: fallback(z.nativeEnum(TripType), TripType.ONE_WAY).default(
      TripType.ONE_WAY,
    ),
    cabinClass: fallback(z.nativeEnum(CabinClass), CabinClass.ANY).default(
      CabinClass.ANY,
    ),
    utm_source: z.string().optional(),
    utm_campaign: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    try {
      getFlightDates(data);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: (error as Error).message,
      });

      return;
    }
  });

export type FlightSearchParams = z.infer<typeof flightSearchSchema>;

function getFlightDates(data: FlightSearchParams) {
  const isRoundTrip = data.tripType === TripType.ROUND_TRIP;

  const { start, end } = computeEffectiveDateRange({
    startOffset: data.departureOffset,
    duration: data.tripLength,
    staticStart: data.departure,
    staticEnd: data.return,
    requireEnd: isRoundTrip,
    allowSameDayEnd: true,
  });

  return { effectiveDeparture: start, effectiveReturn: end };
}

export const Route = createFileRoute("/_authenticated/flightsExternal")({
  component: RouteComponent,
  validateSearch: zodValidator(flightSearchSchema),
  errorComponent: () => (
    <Navigate to="/" search={{ category: "flights" }} replace />
  ),
});

export function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const {
    originId,
    originName,
    originCountry,
    destinationId,
    destinationName,
    destinationCountry,
    adults,
    children,
    infants,
    tripType,
    cabinClass,
  } = searchParams;

  const { updateSearchData } = useFlightSearchDataContext();

  const setSearchData = useEffectEvent(() => {
    const { effectiveDeparture, effectiveReturn } =
      getFlightDates(searchParams);

    updateSearchData({
      from: {
        id: originId,
        name: originName,
        countryName: originCountry,
        type: "CITY",
      },
      to: {
        id: destinationId,
        name: destinationName,
        countryName: destinationCountry,
        type: "CITY",
      },
      dates: {
        departure: effectiveDeparture,
        return: effectiveReturn,
      },
      adult: adults,
      child: children,
      infantWithoutSeat: infants,
      tripType,
      cabinClass,
    });

    navigate({ to: "/flights/departure/list", replace: true });
  });

  useEffect(() => {
    setSearchData();
  }, []);

  return (
    <PageLoader>
      <AnimatedLoader icon="airplane" />
    </PageLoader>
  );
}
